import { Dictionary } from "@/shared/datastructures/Dictionary";
import { Form } from "@/shared/form/Form";
import { SelectionItem, StepperStep } from "@/shared/form/FormTypes";

import { ReportDataGeneralForm } from "../forms/ReportDataGeneralForm";
import { ReportDataImprintForm } from "../forms/ReportDataImprintForm";
import { ReportDataOtherForm } from "../forms/ReportDataOtherForm";
import { ReportDataTaskForm } from "../forms/ReportDataTaskForm";
import { ReportWaterForm } from "../forms/ReportWaterForm";
import { ReportSettingsForm } from "../forms/ReportSettingsForm";
import { BauwerksbuchGeneralForm } from "../forms/BauwerksbuchGeneralForm";
import { BauwerksbuchImprintForm } from "../forms/BauwerksbuchImprintForm";
import { BauwerksbuchTaskForm } from "../forms/BauwerksbuchTaskForm";
import { BauwerksbuchBuildingForm } from "../forms/BauwerksbuchBuildingForm";
import { BauwerksbuchBookInfoForm } from "../forms/BauwerksbuchBookInfoForm";
import { BauwerksbuchDocumentListForm } from "../forms/BauwerksbuchDocumentListForm";
import { BauwerksbuchReportSettingsForm } from "../forms/BauwerksbuchReportSettingsForm";

/**
 * Any concrete form, regardless of the data interface it is typed against.
 *
 * `Form<T>` uses `T` both covariantly (`getData(): T`) and contravariantly
 * (`FormFieldDefinition.validate`), so it is invariant in `T` and has no useful
 * common supertype: `Form<unknown>` rejects every form whose definition is
 * explicitly typed (`ReportSettingsForm`), `Form<never>` rejects `DynamicForm`.
 * The registry only ever wires, initializes and serializes forms, so the data
 * type is genuinely irrelevant here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyForm = Form<any>; // tslint:disable-line no-any

/**
 * One ordered sub-form of a report format. The key is the key the sub-form data
 * is serialized under in the report content payload; the order of the sub-forms
 * is the order of the editor steps.
 */
export interface ReportFormatSubForm {
  key: string;
  createForm: () => AnyForm;
}

/**
 * The single description of one report format. This is the frontend counterpart
 * of the backend report-format registry: adding a format means adding data
 * here, not another branch in the report models or views.
 */
export interface ReportFormatDefinition {
  /** Editor steps. An empty list renders the format without a stepper. */
  steps: StepperStep[];
  /** Ordered sub-forms; `subForms[n]` belongs to `steps[n]`. */
  subForms: ReportFormatSubForm[];
  /** Per-report report settings. Absent means the format shows no settings card. */
  createSettingsForm?: () => AnyForm;
  /** Whether the settings panel offers the hand-written ticket-selection block. */
  hasTicketSelection?: boolean;
}

export const REPORT_FORMAT_DEFAULT = "default";
export const REPORT_FORMAT_WATER_METER = "water_meter_inspection";
export const REPORT_FORMAT_BAUWERKSBUCH = "Bauwerksbuch";

/**
 * The *logical* report formats a checklist can select through its catalog
 * attribute `report_type`. They are the counterpart of the persisted
 * `Report.format` values above; the backend registry knows the same two.
 */
export const CHECKLIST_FORMAT_B1300 = "B1300";
export const CHECKLIST_FORMAT_BAUWERKSBUCH = "Bauwerksbuch";

/** Steps of the existing PDF report (B1300); unchanged from before the refactor. */
const defaultReportSteps: StepperStep[] = [
  {
    label: "Allgemein",
    key: "general",
    editable: true
  },
  {
    label: "Impressum",
    key: "imprint",
    editable: true
  },
  {
    label: "Aufgabenstellung",
    key: "tasks",
    editable: true
  },
  {
    label: "Weitere Kontrollen und Annex",
    key: "other",
    editable: true
  }
];

/**
 * Steps of the Bauwerksbuch report.
 *
 * The step keys select the editor slots, so they must not collide with the
 * steps of the existing PDF report — the two formats share one template.
 *
 * The labels carry no chapter numbers: the chapter structure of the report may
 * change, and the numbering the PDF prints comes from the backend chapter plan
 * at render time, so a number repeated here would silently go stale.
 */
const bauwerksbuchSteps: StepperStep[] = [
  {
    label: "Allgemein",
    key: "bwbGeneral",
    editable: true
  },
  {
    label: "Impressum",
    key: "bwbImprint",
    editable: true
  },
  {
    label: "Aufgabenstellung",
    key: "bwbTask",
    editable: true
  },
  {
    label: "Gebäudebeschreibung",
    key: "bwbBuilding",
    editable: true
  },
  {
    label: "Angaben zum Bauwerksbuch",
    key: "bwbBookInfo",
    editable: true
  },
  {
    label: "Dokumentenliste",
    key: "bwbDocumentList",
    editable: true
  }
];

export const REPORT_FORMATS: Dictionary<ReportFormatDefinition> = {
  [REPORT_FORMAT_DEFAULT]: {
    steps: defaultReportSteps,
    subForms: [
      { key: "general", createForm: () => new ReportDataGeneralForm() },
      { key: "imprint", createForm: () => new ReportDataImprintForm() },
      { key: "task", createForm: () => new ReportDataTaskForm() },
      { key: "other", createForm: () => new ReportDataOtherForm() }
    ],
    createSettingsForm: () => new ReportSettingsForm(),
    hasTicketSelection: true
  },
  [REPORT_FORMAT_WATER_METER]: {
    steps: [],
    subForms: [{ key: "general", createForm: () => new ReportWaterForm() }]
  },
  [REPORT_FORMAT_BAUWERKSBUCH]: {
    steps: bauwerksbuchSteps,
    // the sub-form keys are the content sub-keys of the report snapshot the
    // backend seeds and reads back, so editing rides the existing mutation
    subForms: [
      { key: "general", createForm: () => new BauwerksbuchGeneralForm() },
      { key: "imprint", createForm: () => new BauwerksbuchImprintForm() },
      { key: "task", createForm: () => new BauwerksbuchTaskForm() },
      { key: "building", createForm: () => new BauwerksbuchBuildingForm() },
      { key: "bookInfo", createForm: () => new BauwerksbuchBookInfoForm() },
      {
        key: "documentList",
        createForm: () => new BauwerksbuchDocumentListForm()
      }
    ],
    createSettingsForm: () => new BauwerksbuchReportSettingsForm(),
    hasTicketSelection: true
  }
};

export function reportFormatDefinition(
  reportType: string
): ReportFormatDefinition | undefined {
  return REPORT_FORMATS[reportType];
}

export function reportFormatHasSettings(reportType: string): boolean {
  return reportFormatDefinition(reportType)?.createSettingsForm != null;
}

/**
 * The persisted report format a checklist's logical report format produces.
 *
 * This is the one place the logical→persisted mapping is applied on the
 * frontend. B1300 keeps mapping onto the unchanged `"default"`, so a checklist
 * without an explicitly selected format — a stale cached catalog entry, for
 * instance — still produces exactly the report it produces today.
 */
export function persistedReportFormat(checklistFormat?: string): string {
  return checklistFormat === CHECKLIST_FORMAT_BAUWERKSBUCH
    ? REPORT_FORMAT_BAUWERKSBUCH
    : REPORT_FORMAT_DEFAULT;
}

/**
 * The single PDF report a checklist offers, per its logical report format.
 *
 * The checklist's format *determines* its report, so the Bauwerksbuch option
 * replaces the standard PDF report rather than joining it.
 */
export function checklistReportOption(checklistFormat?: string): SelectionItem {
  return checklistFormat === CHECKLIST_FORMAT_BAUWERKSBUCH
    ? { text: "Bauwerksbuch", value: REPORT_FORMAT_BAUWERKSBUCH }
    : { text: "PDF-Bericht", value: REPORT_FORMAT_DEFAULT };
}
